import { Component, inject, input, output, signal, computed, effect, DestroyRef, ChangeDetectionStrategy, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { RequestService, Request, Benefit, PaymentDecision } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import { DocumentApprovalComponent } from '../../../management-document/components/document-approval/document-approval.component';
import { PartyFormComponent } from '../../../management-parties/components/party-form/party-form.component';
import { ReimbursementService } from '../../../management/reimbursement-management/services/reimbursement.service';
import { FormMode } from 'app/core/constants/form-mode';
import { TimelineComponent, TimelineStep } from 'app/core/components/timeline/timeline.component';
import { FlowBadgeComponent } from 'app/core/components/flow-badge/flow-badge.component';
import { FlowStepsComponent } from 'app/core/components/flow-steps/flow-steps.component';
import { AuthService } from '@services/core/auth.service';
import { CatalogService } from '@services/data/catalog.service';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

const BENEFIT_TYPE_LABELS: Record<string, string> = {
  alarm: 'Alarma',
  sensor: 'Sensor',
  gps: 'GPS',
  security_film: 'Lámina de seguridad',
  roof_rack: 'Portaequipajes',
  hands_free: 'Manos libres',
  glass_engraving: 'Grabado de vidrios',
  license: 'Licencia de conducir',
  notary: 'Gastos notariales',
  rental_car: 'Arriendo / Uber',
  tow_truck: 'Grúa',
  custody: 'Custodia'
};

import { StepApprovalModalComponent, StepApprovalPayload } from '../step-approval-modal/step-approval-modal.component';

@Component({
  selector: 'app-request-benefit',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    DocumentApprovalComponent, PartyFormComponent,
    TimelineComponent, FlowBadgeComponent, FlowStepsComponent,
    StepApprovalModalComponent
  ],
  templateUrl: './request-benefit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./request-benefit.component.scss']
})
export class RequestBenefitComponent {
  request = input.required<Request>();
  reviewCompleted = output<boolean>();

  private requestService = inject(RequestService);
  private reimbursementService = inject(ReimbursementService);
  private bpmService = inject(BpmService);
  private catalogService = inject(CatalogService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private imageOptimizer = inject(ImageOptimizerService);
  private sanitizer = inject(DomSanitizer);
  private modalService = inject(NgbModal);
  private destroyRef = inject(DestroyRef);

  @ViewChild(StepApprovalModalComponent) approvalModal!: StepApprovalModalComponent;
  private activeModal: NgbModalRef | null = null;

  public partyForm!: FormGroup;
  public formMode = FormMode;

  selectedRequest = signal<Request | null>(null);
  currentStep = signal<number>(1);
  isProcessingApproval = signal(false);
  payTxNumber = signal('');
  payDate = signal(new Date().toISOString().split('T')[0]);
  payTransferTarget = signal<'CAMPUS' | 'SOCIO'>('CAMPUS');
  payFile = signal<File | null>(null);
  payFileName = signal('');
  payPreviewUrl = signal<string | null>(null);
  payPreviewKind = signal<'image' | 'pdf' | 'none'>('none');

  private role = computed(() => {
    const roleObj = this.authService.sharingRole() as any;
    return (roleObj?.slug ?? roleObj?.name ?? 'VISUALIZADOR').toUpperCase();
  });

  timelineSteps = computed<TimelineStep[]>(() => [
    { id: 1, title: 'Socio' },
    { id: 2, title: 'Beneficio' },
    { id: 3, title: 'Aprobación' }
  ]);

  currentStepObj = computed(() => {
    const steps = this.timelineSteps();
    const currentId = this.currentStep();
    return steps.find(s => s.id === currentId) ?? null;
  });

  benefit = computed<Benefit | null>(() => this.selectedRequest()?.benefit ?? null);

  vehicle = computed(() => {
    const req = this.selectedRequest();
    return req?.benefit?.vehicle || req?.vehicle || null;
  });

  benefitTypeLabel = computed(() => {
    const type = this.benefit()?.type;
    return type ? (BENEFIT_TYPE_LABELS[type] || type) : 'Beneficio';
  });

  /** Monto vive en la solicitud; al aprobar pasa al reembolso. */
  requestAmount = computed(() => {
    const req = this.selectedRequest();
    const fromReq = req?.amount != null ? Number(req.amount) : null;
    if (fromReq != null && fromReq > 0) return fromReq;
    const reimb = req?.reimbursements?.[0];
    return reimb?.amount != null ? Number(reimb.amount) : null;
  });

  canApprove = computed(() => this.bpmService.canApprove(this.selectedRequest(), this.role()));
  nextStatusSlug = computed(() => this.bpmService.nextStatusSlug(this.selectedRequest()));

  isPaymentStep = computed(() => this.bpmService.isPaymentStep(this.selectedRequest()));

  canRegisterPayment = computed(() => this.canApprove() && this.isPaymentStep());

  approveActionLabel = computed(() => {
    if (this.nextStatusSlug() === 'REIMBURSED') return 'Registrar reembolso';
    return this.isPaymentStep() ? 'Registrar pago' : 'Aprobar beneficio';
  });

  approvalSummary = computed(() => {
    const req = this.selectedRequest();
    if (!req) return null;
    const statusSlug = req.statusSlug || '';
    const isRejected = statusSlug === 'REJECTED';
    const isApproved = ['APPROVED', 'PENDING_PAYMENT', 'PENDING_REIMBURSEMENT', 'PAID', 'REIMBURSED'].includes(statusSlug);
    return {
      statusLabel: isRejected ? 'Rechazado' : (isApproved ? 'Aprobado' : 'Pendiente'),
      isPending: statusSlug === 'PENDING',
      isApproved,
      isRejected,
      rejectionReason: isRejected ? (req.rejectionReason || null) : null,
      approverRole: this.roleLabel(this.bpmService.requiredRole(req)) || 'Secretaría UCH'
    };
  });

  paymentSummary = computed(() => {
    const req = this.selectedRequest();
    if (!req) return null;
    const statusSlug = req.statusSlug || '';
    if (['PENDING', 'REJECTED'].includes(statusSlug)) return null;

    const pd: PaymentDecision | null | undefined = req.paymentDecision;
    const reimb = req.reimbursements?.[0] || null;
    const receiptDoc = reimb?.documents?.[0]?.document || null;
    const isPaid = statusSlug === 'PAID' || statusSlug === 'REIMBURSED';
    const benefitAmount = this.requestAmount();
    const amount = benefitAmount != null
      ? Number(benefitAmount)
      : (reimb?.amount != null ? Number(reimb.amount) : null);

    const statusLabel = isPaid ? (statusSlug === 'REIMBURSED' ? 'Reembolsado' : 'Pagado') : 'Pendiente';

    return {
      statusLabel,
      isPaid,
      isPending: !isPaid,
      decidedAt: isPaid ? (pd?.decidedAt || reimb?.paidAt || null) : null,
      approverName: isPaid ? this.cleanActorLabel(pd?.actorLabel) : null,
      approverMail: isPaid ? (pd?.actorMail || this.mailFromLabel(pd?.actorLabel)) : null,
      approverRole: isPaid
        ? (this.roleLabel(pd?.actorRole) || this.roleLabel(this.bpmService.requiredRole(req)) || 'Tesorero')
        : (this.roleLabel(this.bpmService.requiredRole(req)) || 'Tesorero'),
      channel: this.channelLabel(isPaid ? (pd?.channel || 'INBOX') : 'INBOX'),
      amount,
      paymentDate: isPaid ? (reimb?.paidAt || null) : null,
      transactionNumber: isPaid ? (reimb?.transactionNumber || null) : null,
      transferLabel: isPaid ? this.transferDestinationLabel(reimb?.transferTarget, req) : null,
      hasReceipt: isPaid && !!receiptDoc?.uuid
    };
  });

  payPreviewSafeUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.payPreviewUrl();
    if (!url || this.payPreviewKind() !== 'pdf') return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  payPreviewImgUrl = computed(() => {
    const url = this.payPreviewUrl();
    if (!url || this.payPreviewKind() !== 'image') return null;
    return this.sanitizer.bypassSecurityTrustUrl(url);
  });

  campusPayeeName = computed(() => this.campusNameFromRequest(this.selectedRequest()));
  socioPayeeName = computed(() => this.selectedRequest()?.party?.names || 'el socio');

  payGlossPreview = computed(() => {
    const name = this.payTransferTarget() === 'SOCIO' ? this.socioPayeeName() : this.campusPayeeName();
    return `Tr. ${this.formatDateDot(this.payDate())} ${name}`;
  });

  payBlockedReason = computed(() => {
    if (this.isProcessingApproval()) return 'Procesando...';
    if (!this.payTxNumber().trim()) return 'Indique el número de transacción.';
    if (!this.payDate()) return 'Indique la fecha de pago.';
    if (!this.payFile()) return 'Adjunte el comprobante de pago.';
    return '';
  });

  canConfirmApprove = computed(() => !this.isProcessingApproval());

  constructor() {
    this.partyForm = this.fb.group({
      uuid: [null],
      rut: [null],
      partyTypeCode: [null],
      names: [null],
      birthday: [null],
      mail: [null],
      mobileNumber: [null],
      groupCode: [null],
      entityCode: [null],
    });

    this.destroyRef.onDestroy(() => this.clearPayFile());

    effect(() => {
      const req = this.request();
      const prevUuid = this.selectedRequest()?.uuid;
      this.selectedRequest.set(req);
      if (req.party) this.partyForm.patchValue(req.party);
      if (req.uuid !== prevUuid) {
        this.modalService.dismissAll();
        this.activeModal = null;
        this.clearPayFile();
        this.currentStep.set(this.landingStepFor(req));
      }
    }, { allowSignalWrites: true });
  }

  loadRequests() {
    this.requestService.loadRequests();
  }

  onReviewCompleted() {
    this.reviewCompleted.emit(false);
  }

  openPayModal(): void {
    this.approvalModal?.open();
  }

  closePayModal(): void {
    this.approvalModal?.close();
  }

  onPayTxInput(event: Event): void {
    this.payTxNumber.set((event.target as HTMLInputElement).value ?? '');
  }

  onPayDateInput(event: Event): void {
    this.payDate.set((event.target as HTMLInputElement).value ?? '');
  }

  onPayFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.clearPayFile();
    if (!file) return;
    this.payFile.set(file);
    this.payFileName.set(file.name);
    const url = URL.createObjectURL(file);
    this.payPreviewUrl.set(url);
    if (file.type.startsWith('image/')) this.payPreviewKind.set('image');
    else if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) this.payPreviewKind.set('pdf');
    else this.payPreviewKind.set('none');
  }

  async approveRequest() {
    if (!this.selectedRequest() || this.isProcessingApproval()) return;
    if (this.bpmService.stepRequiresDocument(this.selectedRequest()) || this.isPaymentStep()) {
      this.approvalModal.open();
      return;
    }

    const result = await Swal.fire({
      title: 'Aprobar beneficio',
      text: '¿Confirmar la aprobación y avanzar al siguiente paso?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, aprobar'
    });
    if (!result.isConfirmed) return;
    await this.executeApproval();
  }

  async onStepApprovalConfirmed(payload: StepApprovalPayload): Promise<void> {
    if (!this.selectedRequest()) return;
    if (this.isPaymentStep() && payload.payDetails) {
      await this.executePaymentApproval(payload);
      return;
    }
    await this.executeDocumentApproval(payload);
  }

  private async executePaymentApproval(payload: StepApprovalPayload): Promise<void> {
    this.isProcessingApproval.set(true);
    try {
      const file = payload.file;
      const toUpload = file?.type.startsWith('image/')
        ? ((await this.imageOptimizer.optimize(file)) || file)
        : (file || undefined);
      await firstValueFrom(this.reimbursementService.payByRequest(
        this.selectedRequest()!.uuid,
        payload.payDetails!.txNumber,
        payload.payDetails!.payDate,
        toUpload,
        payload.payDetails!.target
      ));
      this.approvalModal.forceClose();
      this.onReviewCompleted();
      await Swal.fire({
        icon: 'success',
        title: 'Pago registrado',
        text: 'El comprobante quedó guardado y la solicitud quedó Aprobada.'
      });
    } catch (err: any) {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo registrar el pago',
        text: err?.error?.error || 'Intente nuevamente.'
      });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private async executeDocumentApproval(payload: StepApprovalPayload): Promise<void> {
    this.isProcessingApproval.set(true);
    try {
      const nextSlug = this.nextStatusSlug();
      await firstValueFrom(this.requestService.advanceFlowStep(this.selectedRequest()!.uuid, {
        toStatusSlug: nextSlug,
        gloss: payload.gloss,
        documentUuid: payload.documentUuid,
        documentTypeName: payload.documentTypeName,
        requiresDocument: payload.requiresDocument
      }));
      this.approvalModal.forceClose();
      const keepOpen = this.bpmService.shouldStayOpenAfterAdvance(
        this.selectedRequest(),
        nextSlug,
        this.role()
      );
      this.finishAdvance(keepOpen);
    } catch (err: any) {
      const msg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo completar la aprobación',
        text: msg || 'Intente nuevamente.'
      });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  nextStep(): void {
    if (this.currentStep() < this.timelineSteps().length) {
      this.currentStep.update(step => step + 1);
    }
  }

  prevStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update(step => step - 1);
    }
  }

  vehicleLabel(): string {
    const v = this.vehicle();
    if (!v) return 'Sin vehículo';
    const brand = v.brand?.name || v.brandName || '';
    const model = v.model?.name || v.modelName || '';
    return [brand, model].filter(Boolean).join(' ') || 'Vehículo';
  }

  private async executeApproval() {
    if (!this.selectedRequest()) return;
    this.isProcessingApproval.set(true);
    try {
      const nextSlug = this.nextStatusSlug();
      await firstValueFrom(this.requestService.advanceFlowStep(this.selectedRequest()!.uuid, {
        toStatusSlug: nextSlug
      }));
      const keepOpen = this.bpmService.shouldStayOpenAfterAdvance(
        this.selectedRequest(),
        nextSlug,
        this.role()
      );
      this.finishAdvance(keepOpen);
    } catch (err: any) {
      const apiMsg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo completar la aprobación',
        text: apiMsg || 'Intente nuevamente.'
      });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private finishAdvance(keepOpen: boolean): void {
    if (keepOpen) this.goToApproval();
    this.reviewCompleted.emit(keepOpen);
  }

  private goToApproval(): void {
    this.currentStep.set(3);
  }

  private landingStepFor(req: Request): number {
    if (['APPROVED', 'PENDING_PAYMENT', 'PENDING_REIMBURSEMENT', 'PAID', 'REIMBURSED', 'REJECTED'].includes(req.statusSlug || '')) {
      return 3;
    }
    return 1;
  }

  private clearPayFile(): void {
    const url = this.payPreviewUrl();
    if (url) URL.revokeObjectURL(url);
    this.payFile.set(null);
    this.payFileName.set('');
    this.payPreviewUrl.set(null);
    this.payPreviewKind.set('none');
  }

  private campusNameFromRequest(req: Request | null | undefined): string {
    const entity = req?.entity
      || this.catalogService.entities().find(e => Number(e.code) === Number(req?.entityCode));
    return String(entity?.campus || entity?.name || `Entidad ${req?.entityCode ?? ''}`).trim();
  }

  private transferDestinationLabel(target: string | undefined, req: Request): string {
    if (String(target || '').toUpperCase() === 'SOCIO') {
      return `Al socio (${req.party?.names || 'socio'})`;
    }
    return `Al campus (${this.campusNameFromRequest(req)})`;
  }

  private formatDateDot(value: string | null | undefined): string {
    if (!value) return 'N/A';
    const raw = String(value).slice(0, 10);
    const [y, m, d] = raw.split('-');
    if (y && m && d) return `${d}.${m}.${y}`;
    return raw;
  }

  private channelLabel(channel?: string | null): string | null {
    if (!channel) return null;
    const map: Record<string, string> = {
      TOKEN: 'Por token',
      INBOX: 'Bandeja',
      SYSTEM: 'Sistema'
    };
    return map[channel] || channel;
  }

  private roleLabel(role?: string | null): string | null {
    if (!role) return null;
    const map: Record<string, string> = {
      ministro_fe: 'Ministro de Fe',
      sec_uch: 'Secretaría UCH',
      sec: 'Secretaría',
      tesorero_uch: 'Tesorero',
      adm: 'Administrador',
      support: 'Soporte',
      encargado: 'Encargado',
      party: 'Socio'
    };
    return map[role.toLowerCase()] || role;
  }

  private cleanActorLabel(raw?: string | null): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    const mailOnly = trimmed.match(/^\(([^)]+@[^)]+)\)$/);
    if (mailOnly) return mailOnly[1];
    const withMail = trimmed.match(/^(.*?)\s*\(([^)]+@[^)]+)\)\s*$/);
    if (withMail) {
      const name = withMail[1].trim();
      return name || withMail[2];
    }
    return trimmed;
  }

  private mailFromLabel(raw?: string | null): string | null {
    if (!raw) return null;
    const match = raw.match(/\(([^)]+@[^)]+)\)/);
    return match ? match[1] : (raw.includes('@') ? raw : null);
  }
}
